/* Tvak Radiance — theme.js
   Editorial choreography: scroll progress, reveal, letter stagger, parallax,
   chapter detection. Plus the working bits: drawer, cart, qty, variants. */

(() => {
  'use strict';

  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* The motion master gate. True if the merchant has Luxury Motion enabled
     AND the visitor has not requested reduced motion. */
  const motionOn = () =>
    document.documentElement.dataset.motion !== 'off' && !reduced();

  /* Read the parallax intensity from the CSS custom property set by liquid.
     Default 0.05 (5%). Returns a unitless float in [0, 0.10]. */
  const parallaxIntensity = () => {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--parallax-intensity')
      .trim();
    const v = parseFloat(raw);
    return Number.isFinite(v) ? Math.max(0, Math.min(0.10, v)) : 0.05;
  };

  /* ───────── Overlay focus management ────────────────────────────────────
     Move focus into an opened drawer, trap Tab within it, return focus to the
     opener on close, and keep the toggles' aria-expanded in sync. Addresses
     WCAG 2.4.3 (focus order) and 4.1.2 (name/role/value) for the modal drawers. */
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  let activeOverlay = null;
  let lastFocused = null;

  const visibleFocusables = (el) =>
    $$(FOCUSABLE, el).filter((n) => n.offsetWidth || n.offsetHeight || n.getClientRects().length);

  const trapTab = (e) => {
    if (e.key !== 'Tab' || !activeOverlay) return;
    const f = visibleFocusables(activeOverlay);
    if (!f.length) { e.preventDefault(); return; }
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const openOverlay = (el, toggles) => {
    toggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
    if (activeOverlay === el) return;
    lastFocused = document.activeElement;
    activeOverlay = el;
    document.addEventListener('keydown', trapTab, true);
    /* Focus the dialog container itself (tabindex=-1) rather than a child — the
       cart drawer replaces its inner content on refresh, which would detach a
       focused child. Screen readers announce the dialog's role + label. */
    requestAnimationFrame(() => { try { el.focus(); } catch (_) {} });
  };

  const closeOverlay = (el, toggles) => {
    toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    if (activeOverlay !== el) return;
    document.removeEventListener('keydown', trapTab, true);
    activeOverlay = null;
    const ret = lastFocused; lastFocused = null;
    if (ret && ret.focus) requestAnimationFrame(() => { try { ret.focus(); } catch (_) {} });
  };

  /* Announce cart changes to screen readers via the visually-hidden live region. */
  const announce = (msg) => {
    const region = $('[data-cart-announce]');
    if (!region) return;
    region.textContent = '';
    requestAnimationFrame(() => { region.textContent = msg; });
  };

  /* ───────── Drawer ─────────────────────────────────────────────────────── */
  const drawer        = $('[data-drawer]');
  const scrim         = $('[data-scrim]');
  const drawerToggles = $$('[data-drawer-toggle]');

  const setDrawer = (open) => {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', String(!open));
    if (scrim) scrim.setAttribute('aria-hidden', String(!open));
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) openOverlay(drawer, drawerToggles); else closeOverlay(drawer, drawerToggles);
  };
  drawerToggles.forEach((b) =>
    b.addEventListener('click', () => setDrawer(drawer.getAttribute('aria-hidden') === 'true'))
  );

  /* ───────── Cart drawer ────────────────────────────────────────────────── */
  const cartDrawer  = $('[data-cart-drawer]');
  const cartToggles = $$('[data-cart-toggle]');

  const setCart = (open) => {
    if (!cartDrawer) return;
    cartDrawer.setAttribute('aria-hidden', String(!open));
    if (scrim) scrim.setAttribute('aria-hidden', String(!open));
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) openOverlay(cartDrawer, cartToggles); else closeOverlay(cartDrawer, cartToggles);
  };
  cartToggles.forEach((b) =>
    b.addEventListener('click', (e) => {
      e.preventDefault();
      setCart(cartDrawer.getAttribute('aria-hidden') === 'true');
      refreshCart();
    })
  );

  if (scrim) scrim.addEventListener('click', () => { setDrawer(false); setCart(false); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { setDrawer(false); setCart(false); }
  });

  async function refreshCart() {
    if (!cartDrawer) return;
    try {
      const res = await fetch('/?section_id=cart-drawer', { headers: { 'Accept': 'text/html' } });
      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector('[data-cart-drawer-content]');
      const cur  = cartDrawer.querySelector('[data-cart-drawer-content]');
      if (next && cur) cur.replaceWith(next);
    } catch (_) { /* keep existing render */ }
  }

  /* Refresh the full /cart page after a line change. The page is rendered
     from sections/main-cart.liquid; we fetch /cart?section_id=main-cart
     and swap the section content in place — no full-page reload, but the
     cart line items, subtotal, and remove buttons all re-render fresh. */
  async function refreshCartPage() {
    const cartSection = document.querySelector('.template-cart section.container');
    if (!cartSection) return;
    try {
      const res = await fetch('/cart?section_id=main-cart', { headers: { 'Accept': 'text/html' } });
      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector('section.container');
      if (next) cartSection.replaceWith(next);
    } catch (_) { window.location.reload(); }
  }

  const onCartPage = () => document.body.classList.contains('template-cart');

  async function refreshCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.toggleAttribute('hidden', cart.item_count === 0);
      });
    } catch (_) {}
  }

  /* ───────── Add-to-cart (PDP) ──────────────────────────────────────────── */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    if (btn) { btn.dataset.label = btn.textContent; btn.textContent = '…'; btn.disabled = true; }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(form),
      });
      if (!res.ok) throw new Error(await res.text());
      await refreshCartCount();
      await refreshCart();
      setCart(true);
      announce('Item added to your bag.');
    } catch (err) {
      console.warn('Cart add failed', err);
      form.submit();
    } finally {
      if (btn) { btn.textContent = btn.dataset.label || 'Add to bag'; btn.disabled = false; }
    }
  });

  /* ───────── Cart line changes ──────────────────────────────────────────── */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-change]');
    if (!btn) return;
    e.preventDefault();
    const line = parseInt(btn.dataset.line, 10);
    const qty  = parseInt(btn.dataset.quantity, 10);
    if (!line) return;
    if (Number.isNaN(qty)) return;
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line, quantity: qty }),
      });
      if (!res.ok) throw new Error(await res.text());
      await refreshCartCount();
      await refreshCart();
      if (onCartPage()) await refreshCartPage();
      announce('Bag updated.');
    } catch (err) { console.warn('Cart change failed', err); }
  });

  /* ───────── Qty stepper ────────────────────────────────────────────────── */
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (!step) return;
    const wrap  = step.closest('.qty-stepper');
    const input = wrap?.querySelector('input[name="quantity"]');
    if (!input) return;
    const dir  = parseInt(step.dataset.qtyStep, 10);
    const next = Math.max(1, (parseInt(input.value, 10) || 1) + dir);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ───────── Variant picker ─────────────────────────────────────────────── */
  $$('[data-variant-picker]').forEach((picker) => {
    const form = picker.closest('form');
    if (!form) return;
    const select  = form.querySelector('select[name="id"]');
    const variants = (() => { try { return JSON.parse(picker.dataset.variants); } catch { return []; } })();

    picker.addEventListener('change', () => {
      const chosen = $$('input[type="radio"]:checked', picker).map((r) => r.value);
      const match  = variants.find((v) => v.options.every((o, i) => o === chosen[i]));
      if (!match) return;
      if (select) select.value = match.id;
      const priceEl = $('[data-price]', form);
      if (priceEl && match.price_html) priceEl.innerHTML = match.price_html;
      const submit = form.querySelector('[type="submit"]');
      if (submit) {
        submit.disabled = !match.available;
        submit.textContent = match.available
          ? (submit.dataset.labelAdd || 'Add to bag')
          : (submit.dataset.labelSold || 'Sold out');
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    });
  });

  /* ───────── Reveal on scroll ─────────────────────────────────────────────
     Gated by motionOn(). When motion is off, reveals are immediate so the
     content remains visible — never gated content behind motion. */
  if ('IntersectionObserver' in window && motionOn()) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -10% 0px' });
    $$('.reveal, .reveal-stagger').forEach((el) => io.observe(el));
  } else {
    $$('.reveal, .reveal-stagger').forEach((el) => el.classList.add('is-in'));
  }

  /* ───────── Letter-stagger reveal ──────────────────────────────────────────
     For [data-letters], wrap each character in <span class="letter"> and
     reveal them one-by-one. Honors prefers-reduced-motion. */
  $$('[data-letters]').forEach((el) => {
    if (!motionOn()) { return; }
    const text = el.textContent;
    el.textContent = '';
    [...text].forEach((ch, i) => {
      const span = document.createElement('span');
      span.className = 'letter';
      span.textContent = ch === ' ' ? ' ' : ch;
      span.style.transitionDelay = `${i * 28}ms`;
      el.appendChild(span);
    });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        $$('.letter', el).forEach((s) => s.classList.add('is-in'));
      });
    });
  });

  /* ───────── Scroll progress ──────────────────────────────────────────────
     A 1px hairline at the top, transform: scaleX (GPU-composited, no reflow). */
  (function scrollProgress() {
    if (!motionOn()) return;
    const bar = document.createElement('div');
    bar.className = 'scroll-progress';
    document.body.prepend(bar);
    let raf = 0;
    const update = () => {
      raf = 0;
      const max = (document.documentElement.scrollHeight - window.innerHeight) || 1;
      const pct = Math.max(0, Math.min(1, window.scrollY / max));
      // Write a unitless 0..1 — base.css uses scaleX(var(--scroll-progress)).
      bar.style.setProperty('--scroll-progress', pct.toFixed(4));
    };
    window.addEventListener('scroll', () => {
      if (!raf) raf = requestAnimationFrame(update);
    }, { passive: true });
    update();
  })();

  /* ───────── Parallax ─────────────────────────────────────────────────────
     Two patterns:
     • [data-parallax="0.05"] on plain elements → translate-only (chambers)
     • .parallax-frame → image scaled 105% inside a fixed frame, drifts on Y.
     Both share the same scroll handler. Travel is bounded to the merchant's
     parallax_intensity (typically 5%) so the image never reveals its edges. */
  (function parallax() {
    if (!motionOn()) return;

    const intensityCap = parallaxIntensity();   // 0..0.10
    if (intensityCap === 0) return;

    const translateEls = $$('[data-parallax]');
    const frameImgs    = $$('.parallax-frame > img, .parallax-frame > picture > img');
    if (!translateEls.length && !frameImgs.length) return;

    let raf = 0;
    const update = () => {
      raf = 0;
      const vh = window.innerHeight;

      // Translate-only pattern: factor comes from data-parallax, capped by intensityCap.
      translateEls.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const requested = parseFloat(el.dataset.parallax) || 0.05;
        const factor = Math.min(requested, intensityCap * 2); // legacy targets allow 2× cap
        const center = rect.top + rect.height / 2;
        const offset = (center - vh / 2) * -factor;
        el.style.setProperty('--parallax-y', `${offset.toFixed(1)}px`);
      });

      // Frame pattern: image is 105% of frame; max drift = (frameHeight × intensityCap)
      // halved (so it can travel up OR down by half the headroom).
      frameImgs.forEach((img) => {
        const frame = img.closest('.parallax-frame');
        if (!frame) return;
        const rect = frame.getBoundingClientRect();
        const headroom = rect.height * intensityCap;       // 5% of frame height
        const maxTravel = headroom / 2;
        // Position 0 when frame center == viewport center; ±1 at edges.
        const t = Math.max(-1, Math.min(1, (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2)));
        const offset = -t * maxTravel;
        img.style.setProperty('--parallax-y', `${offset.toFixed(1)}px`);
      });
    };

    window.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
    window.addEventListener('resize', update);
    update();
  })();

  /* ───────── Chapter index — active state for the sticky sidebar ────────── */
  (function chapters() {
    const links    = $$('[data-chapter-link]');
    const chambers = $$('[data-chapter]');
    if (!links.length || !chambers.length || !('IntersectionObserver' in window)) return;

    const setActive = (id) => {
      links.forEach((a) => a.classList.toggle('is-active', a.dataset.chapterLink === id));
    };
    const io = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      if (visible[0]) {
        const id = visible[0].target.dataset.chapter;
        setActive(id);
        visible[0].target.classList.add('is-in');
      }
    }, { threshold: [0.25, 0.5, 0.75], rootMargin: '-20% 0px -30% 0px' });
    chambers.forEach((c) => io.observe(c));

    /* Smooth-scroll the chapter links */
    links.forEach((a) => {
      a.addEventListener('click', (e) => {
        const id = a.dataset.chapterLink;
        const target = chambers.find((c) => c.dataset.chapter === id);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
      });
    });
  })();

  /* ───────── Boot ────────────────────────────────────────────────────────── */
  refreshCartCount();
})();
